import {DragDropContext} from '@wandb/weave/common/containers/DragDropContainer';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import _ from 'lodash';
import React, {FC, memo, useContext, useEffect, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {usePanelComments} from '../../state/reports/usePanelComments';
import {PanelCompRedux} from '../PanelCompRedux';
import {
  useWorkspaceDrawerContextMaybe,
  WorkspaceDrawerContextType,
} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {useConfigXBounds} from './hooks';
import {usePanelContext} from './PanelContextProvider';
import {PanelEditorContainer} from './PanelEditorContainer';
import {PanelHeader} from './PanelHeader';
import {PanelIDContextProvider} from './PanelIDContext';
import {SharedPanelStateContextProvider} from './SharedPanelStateContext';
import {PanelPopupMode, PanelProps} from './types';

const EditablePanelComp: FC<PanelProps> = ({
  currentHeight,
  defaultMoveToSectionRef,
  disableRunLinks,
  onContentHeightChange,
  shouldRenderContent = true,
  shouldTurnOnComments,
}) => {
  const {isReadOnly, panelRef, panel, setPanelPopupMode} = usePanelContext();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const panelId = searchParams.get('panelId');
  const isReport = useIsInLoadedReportContext();

  // Open panel editor only if panelId matches the current panel's ID
  useEffect(() => {
    if (isReport) {
      return;
    }
    if (panelId === panel.__id__) {
      setPanelPopupMode(PanelPopupMode.FULL_SCREEN);
    } else {
      setPanelPopupMode(undefined);
    }
  }, [panelId, panel.__id__, setPanelPopupMode, isReport]);

  const panelType = panel.viewType;

  const {isHighlighted} = usePanelComments({panelRef});

  const {dragRef} = useContext(DragDropContext);
  const isSelected = useMemo(
    () => dragRef?.id === panelRef.id,
    [panelRef, dragRef]
  );

  const panelViewTypeClass = useMemo(
    () => panelType.toLowerCase().replace(' ', '-') + '-container',
    [panelType]
  );

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'editable-panel-markdown': panelType === 'Markdown Panel',
      'panel-selected': isSelected,
      'panel-highlighted': isHighlighted,
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [
    panelViewTypeClass,
    panelType,
    isSelected,
    isHighlighted,
    shouldRenderContent,
  ]);

  const configXBounds = useConfigXBounds(panelRef);
  const workspaceDrawerContext = useWorkspaceDrawerContextMaybe();

  let workspaceDrawerTarget:
    | undefined
    | WorkspaceDrawerContextType['targetPanelRef'] = undefined;
  if (workspaceDrawerContext) {
    workspaceDrawerTarget = workspaceDrawerContext.targetPanelRef;
  }

  const isWorkspaceDrawerTarget = useMemo(
    () => _.isEqual(panelRef, workspaceDrawerTarget),
    [panelRef, workspaceDrawerTarget]
  );

  return (
    <PanelIDContextProvider stablePanelId={panel.__id__}>
      <SharedPanelStateContextProvider config={configXBounds}>
        <PanelEditorContainer
          currentHeight={currentHeight}
          disableRunLinks={disableRunLinks}
        />
        <TailwindContents>
          <div
            className={twMerge(
              panelClassNames,
              'px-12 pb-12 pt-4',
              isWorkspaceDrawerTarget &&
                'border-gold-350 shadow-[0px_0px_0px_4px_rgb(255,230,168)]'
            )}
            data-test="panel">
            <PanelHeader
              defaultMoveToSectionRef={defaultMoveToSectionRef}
              shouldTurnOnComments={shouldTurnOnComments}
            />
            <div
              className={twMerge(
                'flex flex-col',
                'h-full w-full grow-[1]',
                'justify-center',
                // Applying this to other panels may cause infinite ResizeObserver loop. Please proceed with caution
                // if you are loosening this check.
                (panelType === 'Code Comparer' ||
                  panelType === 'Media Browser' ||
                  panelType === 'Markdown Panel') &&
                  'overflow-auto'
              )}
              // editablePanelDOMID from reportExport.ts, not imported for code-splitting
              id={'editable-panel-' + panel.__id__}>
              {/* TODO - refactor so PanelCompRedux can use Context/Provider */}
              <PanelCompRedux
                configMode={false}
                currentHeight={currentHeight}
                dimensions={undefined as any}
                disableRunLinks={disableRunLinks}
                onContentHeightChange={onContentHeightChange}
                panelRef={panelRef}
                readOnly={isReadOnly}
              />
            </div>
          </div>
        </TailwindContents>
      </SharedPanelStateContextProvider>
    </PanelIDContextProvider>
  );
};

export const EditablePanel = memo(EditablePanelComp, (prevProps, nextProps) => {
  const didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'EditablePanel',
    deep: ['pageQuery', 'panel'],
    ignoreFunctions: true,
    ignoreJSX: true,
    debug: false,
    verbose: false,
  });

  return didPropsOrStateChange(prevProps, nextProps);
});
